<template>
  <section class="c2c-queue" :aria-label="t('queue.aria_label')">
    <header class="c2c-queue-head">
      <h2>{{ t('queue.heading', { n: items.length }) }}</h2>
      <span class="c2c-queue-summary">
        {{ t('queue.summary', { done: doneCount, total: items.length }) }}
      </span>
    </header>

    <!-- Everything encodes in this tab, one file at a time. Worth saying
         before someone queues forty videos and closes the laptop. -->
    <p v-if="items.length >= LONG_QUEUE_NOTICE_AT" class="c2c-queue-note">
      {{ t('queue.long_queue_note', { n: items.length }) }}
    </p>

    <ul class="c2c-queue-list">
      <li
        v-for="(item, i) in items"
        :key="item.id"
        class="c2c-queue-item"
        :class="{
          'c2c-queue-item--current': i === currentIndex,
          'c2c-queue-item--done': item.status === 'done',
          'c2c-queue-item--error': item.status === 'error',
          'c2c-queue-item--cancelled': item.status === 'cancelled',
          'c2c-queue-item--clickable': isClickable(item, i),
        }"
        @click="isClickable(item, i) && $emit('select', item.id)"
      >
        <div class="c2c-queue-item-icon" aria-hidden="true">
          <span v-if="item.status === 'done'" class="c2c-queue-icon-done"><Check :size="15" /></span>
          <span v-else-if="item.status === 'error'" class="c2c-queue-icon-err"><X :size="15" /></span>
          <span v-else-if="item.status === 'cancelled'" class="c2c-queue-icon-cancel"><Minus :size="15" /></span>
          <span v-else-if="i === currentIndex" class="c2c-queue-icon-active">{{ i + 1 }}</span>
          <span v-else class="c2c-queue-icon-pending">{{ i + 1 }}</span>
        </div>

        <div class="c2c-queue-item-body">
          <div class="c2c-queue-item-title">{{ item.form.title || item.file.name }}</div>
          <div class="c2c-queue-item-meta">
            <span>{{ formatBytes(item.file.size) }}</span>
            <span v-if="item.meta">· {{ Math.round(item.meta.duration) }}s</span>
            <span class="c2c-queue-item-status">· {{ statusLabel(item.status) }}</span>
          </div>
          <div
            v-if="(item.status === 'encoding' || item.status === 'uploading') && item.progress > 0"
            class="c2c-queue-bar"
            role="progressbar"
            :aria-valuenow="Math.round(item.progress * 100)"
            aria-valuemin="0" aria-valuemax="100"
          >
            <div
              class="c2c-queue-bar-fill"
              :class="{ 'c2c-queue-bar-fill--upload': item.status === 'uploading' }"
              :style="{ width: Math.round(item.progress * 100) + '%' }"
            ></div>
          </div>
          <div v-if="item.status === 'error' && item.errorMessage" class="c2c-queue-item-err">
            {{ item.errorMessage }}
          </div>
        </div>

        <div class="c2c-queue-item-actions">
          <a
            v-if="item.status === 'done' && item.result"
            :href="item.result.pageUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="c2c-queue-item-link"
          >{{ t('queue.view') }}</a>
          <button
            v-if="canRemove(item, i)"
            type="button"
            class="c2c-queue-item-remove"
            :aria-label="t('queue.remove_aria', { name: item.file.name })"
            @click.stop="askRemove(item)"
          ><X :size="16" /></button>
        </div>
      </li>
    </ul>

    <!-- Removing is destructive and the × sits inside a row that is itself
         clickable, so a mis-aimed click used to both switch the editor and
         drop a video. Now it asks first. -->
    <AppDialog
      :visible="!!pendingRemove"
      tone="warn"
      :title="t('queue.remove_confirm_title')"
      :message="t('queue.remove_confirm_message', { name: pendingRemove?.file.name ?? '' })"
      :confirm-label="t('queue.remove_confirm_yes')"
      :cancel-label="t('queue.remove_confirm_no')"
      @confirm="confirmRemove"
      @cancel="pendingRemove = null"
    />
  </section>
</template>

<script setup lang="ts">
import { computed, ref } from "vue"
import { Check, Minus, X } from "lucide-vue-next"
import { useI18n } from "vue-i18n"
import AppDialog from "@/components/AppDialog.vue"
import { LONG_QUEUE_NOTICE_AT } from "@/lib/config"
import type { BatchItem, BatchItemStatus } from "@/lib/types"

const { t } = useI18n()

const props = defineProps<{
  items: BatchItem[]
  currentIndex: number
}>()


const emit = defineEmits<{
  remove: [id: string],
  select: [id: string]
}>()

/** The video the user is being asked about, or null when no dialog is open. */
const pendingRemove = ref<BatchItem | null>(null)

function askRemove(item: BatchItem) {
  pendingRemove.value = item
}

function confirmRemove() {
  const item = pendingRemove.value
  pendingRemove.value = null
  if (item) emit("remove", item.id)
}

const doneCount = computed(() =>
  props.items.filter(i => i.status === "done").length)

function statusLabel(s: BatchItemStatus): string {
  switch (s) {
    case "pending": return t('queue.status_pending')
    case "analyzing": return t('queue.status_analyzing')
    case "encoding": return t('queue.status_encoding')
    case "uploading": return t('queue.status_uploading')
    case "done": return t('queue.status_done')
    case "error": return t('queue.status_error')
    case "cancelled": return t('queue.status_cancelled')
  }
}

/** Items that have already been processed (or are currently processing) can't
 *  be removed. Pending items waiting their turn can. */
function canRemove(item: BatchItem, i: number): boolean {
  if (i === props.currentIndex) return false
  return item.status === "pending"
}

/** Whether an item can be clicked to switch editing context. Only pending
 *  items that aren't already active are valid targets. */
function isClickable(item: BatchItem, i: number): boolean {
  return item.status === 'pending' && i !== props.currentIndex
}

function formatBytes(bytes: number): string {
  if (!bytes || bytes < 0) return "0 B"
  if (bytes < 1024) return bytes + " B"
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB"
  if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(2) + " MB"
  return (bytes / (1024 * 1024 * 1024)).toFixed(2) + " GB"
}

</script>

<style scoped>
.c2c-queue {
  margin-top: 16px;
  padding: 16px;
  background: white;
  border: 1px solid #eaecf0;
  border-radius: 6px;
}
.c2c-queue-head {
  display: flex; justify-content: space-between; align-items: baseline;
  gap: 12px; flex-wrap: wrap; margin-bottom: 12px;
}
.c2c-queue-head h2 { margin: 0; font-size: 16px; }
.c2c-queue-summary { font-size: 13px; color: #54595d; }

/* The long-queue warning. It shipped with markup and no rule, so it
   rendered as a bare paragraph jammed against the list below it. */
.c2c-queue-note {
  margin: 0 0 10px 0;
  padding: 8px 10px;
  font-size: 12px;
  line-height: 1.45;
  color: #886600;
  background: #fef6e7;
  border: 1px solid #f0d795;
  border-radius: 6px;
}

.c2c-queue-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 6px; }
.c2c-queue-item {
  display: grid;
  grid-template-columns: 32px 1fr auto;
  gap: 10px;
  align-items: center;
  padding: 10px;
  border: 1px solid var(--border-color-muted, #eaecf0);
  border-radius: 6px;
  background: var(--background-color-interactive-subtle, #f8f9fa);
  transition: background-color 120ms ease-out, border-color 120ms ease-out;
}
/* The active row gets a spine rather than only a wash, so the eye finds
   "where am I" without reading the status text. */
.c2c-queue-item--current {
  background: #eaf3ff;
  border-color: #c8d6f0;
  box-shadow: inset 2px 0 0 var(--background-color-progressive, #36c);
}
.c2c-queue-item--done { background: #f3faf7; border-color: #c5e5da; }
.c2c-queue-item--error { background: #fee7e6; border-color: #f0b0b0; }
.c2c-queue-item--cancelled { opacity: 0.55; }

.c2c-queue-item--clickable { cursor: pointer; }
.c2c-queue-item--clickable:hover { background: #eef3fc; border-color: #b0c4e8; }
.c2c-queue-item--done,
.c2c-queue-item--error,
.c2c-queue-item--cancelled { cursor: not-allowed; }

.c2c-queue-item-icon {
  width: 32px; height: 32px;
  display: flex; align-items: center; justify-content: center;
}
/*
 * The coloured disc is the inner span, not this wrapper. It used to be the
 * other way round - the wrapper carried border-radius with no background
 * while the span carried a background with no size - so the "circle" was
 * really a tight rectangle hugging the glyph. Sizing the span fixes that,
 * and keeps the disc identical whether it holds a digit or a tick.
 */
.c2c-queue-item-icon > span :deep(svg) { display: block; }
.c2c-queue-item-icon > span {
  width: 26px; height: 26px;
  border-radius: 50%;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 13px; font-weight: 700; line-height: 1;
  /* tabular figures so 9 -> 10 cannot nudge the row, and an explicit stack
     so the tick and cross render as glyphs rather than colour emoji. */
  font-variant-numeric: tabular-nums;
  font-family: system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
  transition: background-color 120ms ease-out, box-shadow 120ms ease-out;
}
/* The tick and cross read small at digit size. */
.c2c-queue-icon-done,
.c2c-queue-icon-err,
.c2c-queue-icon-cancel { font-size: 15px; }

.c2c-queue-icon-done {
  background: var(--color-success, #177860);
  color: var(--color-inverted-fixed, #fff);
}
.c2c-queue-icon-err {
  background: var(--color-error, #bf3c2c);
  color: var(--color-inverted-fixed, #fff);
}
.c2c-queue-icon-cancel {
  background: var(--border-color-base, #a2a9b1);
  color: var(--color-inverted-fixed, #fff);
}
.c2c-queue-icon-active {
  background: var(--background-color-progressive, #36c);
  color: var(--color-inverted-fixed, #fff);
  box-shadow: 0 0 0 3px rgba(51, 102, 204, 0.18);
}
/* Pending is an outline, not a filled grey disc: queued work should not
   carry as much weight as work that is happening or finished. */
.c2c-queue-icon-pending {
  background: transparent;
  color: var(--color-subtle, #54595d);
  box-shadow: inset 0 0 0 1.5px var(--border-color-subtle, #c8ccd1);
}

.c2c-queue-item-body { min-width: 0; }
.c2c-queue-item-title {
  font-size: 14px; font-weight: 600;
  word-break: break-word;
}
.c2c-queue-item-meta {
  font-size: 12px; color: var(--color-subtle, #54595d);
  display: flex; gap: 6px; flex-wrap: wrap;
  margin-top: 3px;
  font-variant-numeric: tabular-nums;
}
.c2c-queue-item-status { font-weight: 600; }
.c2c-queue-item-err {
  font-size: 12px; color: var(--color-error, #bf3c2c);
  margin-top: 4px;
  word-break: break-word;
}

.c2c-queue-bar {
  height: 4px;
  background: var(--background-color-neutral, #eaecf0);
  border-radius: 2px; overflow: hidden; margin-top: 8px;
}
.c2c-queue-bar-fill {
  height: 100%;
  background: var(--background-color-progressive, #36c);
  border-radius: 2px;
  transition: width 160ms ease-out;
}
.c2c-queue-bar-fill--upload { background: var(--color-success, #177860); }

.c2c-queue-item-actions { display: flex; gap: 8px; align-items: center; flex-shrink: 0; }
.c2c-queue-item-link { font-size: 12px; color: #3366cc; text-decoration: none; }
.c2c-queue-item-link:hover { text-decoration: underline; }
/* Was a borderless 24px glyph that read as decoration. It is a destructive
   control, so it looks like a button and meets the 32px touch target. */
.c2c-queue-item-remove {
  background: #fff;
  border: 1px solid var(--border-color-base, #a2a9b1);
  color: var(--color-subtle, #54595d);
  cursor: pointer; font-size: 17px;
  width: 32px; height: 32px;
  border-radius: 6px; line-height: 1;
  display: inline-flex; align-items: center; justify-content: center;
  transition: background-color 120ms ease-out, border-color 120ms ease-out, color 120ms ease-out;
}
.c2c-queue-item-remove:hover {
  background: #fee7e6;
  border-color: var(--color-error, #bf3c2c);
  color: var(--color-error, #bf3c2c);
}
.c2c-queue-item-remove:focus-visible {
  outline: 2px solid var(--background-color-progressive, #36c);
  outline-offset: 1px;
}

@media (max-width: 540px) {
  .c2c-queue-item { grid-template-columns: 32px 1fr; }
  .c2c-queue-item-actions { grid-column: 1 / -1; justify-content: flex-end; }
}
</style>
